import { Button, Image, Popconfirm, Space, Typography } from 'antd'
import { type ChangeEvent, useRef, useState } from 'react'
import { MdDeleteOutline, MdOutlineAddPhotoAlternate } from 'react-icons/md'

import { restClient } from '@/Core'
import { HEIC_ACCEPT, preparePhoto } from '@/Ship/Helpers/heicPhoto'
import { getPhotoValidationError } from '@/Ship/Helpers/photoRules'

const { Text } = Typography

/** Ліміт фото на зміну — як на бекенді (UploadShiftPhotoAction::MAX_PHOTOS) */
const MAX_PHOTOS = 10

export interface ShiftPhoto {
    uuid: string
    url: string
    originalName?: string | null
}

interface Props {
    shiftUuid: string
    photos: ShiftPhoto[]
    /** Додавати й видаляти — диспетчер у завершеній зміні (`_permissions.photos`) */
    canManage: boolean
    /** Після додавання чи видалення — перечитати картку */
    onChanged: () => void
}

const errorText = (err: any, fallback: string): string => {
    const violations = err?.response?.violations
    if (Array.isArray(violations) && violations[0]?.message) return violations[0].message
    return err?.response?.message || fallback
}

/**
 * Фото несправностей і пошкоджень ТЗ, додані водієм під час закриття зміни. Мініатюри з
 * переглядом; диспетчер може додати (з тим самим стисненням і лімітами, що й у водія) або видалити.
 */
export const ShiftPhotos = ({ shiftUuid, photos, canManage, onChanged }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const upload = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? [])
        event.target.value = ''
        if (files.length === 0) return

        setBusy(true)
        setError(null)
        const errors: string[] = []
        let added = 0
        try {
            for (const file of files) {
                if (photos.length + added >= MAX_PHOTOS) {
                    errors.push(`До зміни можна додати не більше ${MAX_PHOTOS} фото`)
                    break
                }
                let compressed: File
                try {
                    // HEIC з iPhone → JPEG, далі стиснення під ліміт
                    compressed = await preparePhoto(file)
                } catch (err) {
                    errors.push(err instanceof Error ? err.message : `«${file.name}»: не вдалося обробити фото`)
                    continue
                }
                const invalid = getPhotoValidationError(compressed)
                if (invalid) {
                    errors.push(invalid)
                    continue
                }
                const body = new FormData()
                body.append('file', compressed, compressed.name)
                try {
                    await restClient.post(
                        `/services/shifts/${shiftUuid}/photos`,
                        { body },
                        { isNotHeaderAddContentType: true }
                    )
                    added++
                } catch (err) {
                    errors.push(errorText(err, `«${file.name}»: не вдалося завантажити`))
                }
            }
        } finally {
            setBusy(false)
            setError(errors.length > 0 ? errors.join('\n') : null)
            if (added > 0) onChanged()
        }
    }

    const remove = async (photo: ShiftPhoto) => {
        setBusy(true)
        setError(null)
        try {
            await restClient.delete(`/services/shifts/${shiftUuid}/photos/${photo.uuid}`)
            onChanged()
        } catch (err) {
            setError(errorText(err, 'Не вдалося видалити фото'))
        } finally {
            setBusy(false)
        }
    }

    return (
        <div data-testid="shift-photos">
            {photos.length === 0 && !canManage && <Text type="secondary">Фото немає</Text>}
            {photos.length > 0 && (
                <Image.PreviewGroup>
                    <Space wrap size={10}>
                        {photos.map((photo) => (
                            <div key={photo.uuid} style={{ position: 'relative' }}>
                                <Image
                                    src={photo.url}
                                    alt={photo.originalName ?? 'Фото несправності'}
                                    width={88}
                                    height={88}
                                    style={{ objectFit: 'cover', borderRadius: 6 }}
                                />
                                {canManage && (
                                    <Popconfirm
                                        title="Видалити фото?"
                                        okText="Видалити"
                                        cancelText="Скасувати"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() => remove(photo)}
                                    >
                                        <Button
                                            size="small"
                                            danger
                                            shape="circle"
                                            icon={<MdDeleteOutline />}
                                            aria-label="Видалити фото"
                                            disabled={busy}
                                            style={{ position: 'absolute', top: 4, right: 4 }}
                                        />
                                    </Popconfirm>
                                )}
                            </div>
                        ))}
                    </Space>
                </Image.PreviewGroup>
            )}
            {canManage && (
                <div style={{ marginTop: photos.length > 0 ? 10 : 0 }}>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={`image/*,${HEIC_ACCEPT}`}
                        multiple
                        hidden
                        onChange={upload}
                    />
                    <Button
                        icon={<MdOutlineAddPhotoAlternate />}
                        loading={busy}
                        disabled={photos.length >= MAX_PHOTOS}
                        onClick={() => inputRef.current?.click()}
                    >
                        Додати фото
                    </Button>
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                        {photos.length} з {MAX_PHOTOS}
                    </Text>
                </div>
            )}
            {error && (
                <div style={{ marginTop: 6 }}>
                    <Text type="danger" style={{ whiteSpace: 'pre-line' }}>
                        {error}
                    </Text>
                </div>
            )}
        </div>
    )
}
